"use strict";
/**
 * 联系方式的规范化与链接生成。
 *
 * 放在 contracts 里，是因为它有**三个调用方**，而三处必须得到同一个结果：
 *   - 控制台：边填边显示"链接会生成成这样"，客户当场能看出号码填错；
 *   - 平台侧：保存前清洗，库里只存一种形态；
 *   - 渲染层：页脚、浮动按钮、结构化数据都从同一个值派生。
 *
 * 任何一处自己写一遍 `wa.me` 的拼接，都会在某个国家的号码上跟另外两处不一致。
 * 这里不依赖任何 Node 内置模块 —— 控制台在浏览器里跑，也要能 import。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY_CONTACT = void 0;
exports.normalizeWhatsapp = normalizeWhatsapp;
exports.whatsappLink = whatsappLink;
exports.telegramLink = telegramLink;
exports.normalizeQrUrl = normalizeQrUrl;
exports.normalizeContact = normalizeContact;
exports.hasAnyContact = hasAnyContact;
/**
 * WhatsApp 号码的规范形态：**纯数字、带国家码、不带前导 0**。
 *
 * 客户实际会填成什么样：`+86 138-0013-8000`、`0086 138 0013 8000`、
 * `(86)13800138000`。这些在 `wa.me` 上全部打不开 —— 它只收数字。
 *
 * 剥离规则里唯一有判断的一步是**去前导 `00`**：那是国际直拨前缀（IDD），
 * 与 `+` 等价，留着会拼出 `wa.me/0086138...`，WhatsApp 会当成一个不存在的号码。
 *
 * 返回 `null` 表示这个值不能当号码用（长度不对、去掉前缀后以 0 开头）。
 * 不猜国家码：客户填 `13800138000` 时我们无法知道他在哪个国家，
 * 猜一个 `+86` 会把越南客户的号码指到中国去。控制台靠"实时预览链接"让人自己发现。
 */
function normalizeWhatsapp(raw) {
    if (!raw)
        return null;
    let digits = raw.replace(/\D/g, '');
    if (digits.startsWith('00'))
        digits = digits.slice(2);
    // E.164 上限 15 位、下限取 8：再短的号在任何国家都不是手机号
    if (digits.length < 8 || digits.length > 15)
        return null;
    // 前导 0 是国内长途前缀，国际格式里不允许出现
    if (digits.startsWith('0'))
        return null;
    return digits;
}
/**
 * 生成 `wa.me` 链接。
 *
 * `text` 是点开对话框时预先填好的那句话。外贸站的实际用法是
 * "Hi, I saw your website and would like to ask about…" —— 采购商不用自己想开头，
 * 回复率差别不小。这里**不做 i18n**：文案由调用方按页面语言传进来。
 */
function whatsappLink(number, text) {
    const digits = normalizeWhatsapp(number);
    if (!digits)
        return null;
    const base = `https://wa.me/${digits}`;
    if (!text)
        return base;
    return `${base}?text=${encodeURIComponent(text)}`;
}
/**
 * Telegram 用户名归一成 `t.me` 链接。
 *
 * 三种输入都接受：`@acme_sales`、`acme_sales`、`https://t.me/acme_sales`。
 * 客户不会记得"要不要带 @"，而带不带 @ 在 Telegram 里是同一个账号。
 *
 * 用户名规则（官方）：5–32 位，字母开头，只含字母数字下划线。
 * 这里放宽到 4 位起：早期注册的账号确实存在更短的历史用户名。
 */
function telegramLink(raw) {
    if (!raw)
        return null;
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    // 已经是完整链接的直接透传，只接受 t.me / telegram.me 这两个官方域
    const asUrl = /^https?:\/\/(?:www\.)?(?:t\.me|telegram\.me)\/([A-Za-z0-9_]{4,32})\/?$/i.exec(trimmed);
    if (asUrl)
        return `https://t.me/${asUrl[1]}`;
    const handle = trimmed.replace(/^@/, '');
    if (!/^[A-Za-z][A-Za-z0-9_]{3,31}$/.test(handle))
        return null;
    return `https://t.me/${handle}`;
}
/**
 * 微信二维码地址只接受 http(s) 绝对地址或站内相对路径。
 *
 * 微信没有可点击的公开主页（`weixin://` 在浏览器里点不动），
 * 所以这一项存的是**图片地址**，由前台直接渲染成图。
 * `javascript:` 与 `data:` 一律拒绝 —— 这个值会进 `<img src>`，
 * 一个 `data:image/svg+xml` 里可以带脚本。
 */
function normalizeQrUrl(raw) {
    if (!raw)
        return null;
    const value = raw.trim();
    if (!value)
        return null;
    if (value.startsWith('/') && !value.startsWith('//'))
        return value;
    if (/^https?:\/\/[^\s]+$/i.test(value))
        return value;
    return null;
}
/** 空壳。渲染层读旧数据时，这几个键可能根本不存在 */
exports.EMPTY_CONTACT = {
    email: null,
    phone: null,
    address: null,
    whatsapp: null,
    telegram: null,
    wechatQrUrl: null,
};
/**
 * 从库里的 jsonb 安全读出一份 `SiteContact`。
 *
 * 必须容错：这一列是 `settings` 里的一个键，2019 年之前建的站点、
 * 或者被人工改过的行，都可能缺字段、或者把 `email` 存成数字。
 * 渲染层因为一个联系电话格式不对就整页 500，是最不划算的失败方式。
 */
function normalizeContact(raw) {
    if (typeof raw !== 'object' || raw === null)
        return { ...exports.EMPTY_CONTACT };
    const source = raw;
    const str = (key) => {
        const value = source[key];
        if (typeof value !== 'string')
            return null;
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
    };
    return {
        email: str('email'),
        phone: str('phone'),
        address: str('address'),
        whatsapp: normalizeWhatsapp(str('whatsapp')),
        telegram: str('telegram'),
        wechatQrUrl: normalizeQrUrl(str('wechatQrUrl')),
    };
}
/** 有没有任何一个可点击的联系渠道。页脚据此决定要不要渲染整块 */
function hasAnyContact(contact) {
    return Boolean(contact.email ||
        contact.phone ||
        contact.whatsapp ||
        contact.telegram ||
        contact.wechatQrUrl ||
        contact.address);
}
//# sourceMappingURL=contact.js.map